import { OptionalMark } from "@patient-watch/forms";
import { useParams } from "@tanstack/react-router";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ui } from "@patient-watch/ui";
import type { InputT } from "@patient-watch/types";
import Slider from "@/components/input/Slider";
import { useBaselineEntryValueByInputIdAndTrackerId } from "@/features/entry-values/api/selectBaselineEntryValueByInputIdAndTrackerId";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { useMsAdlCopy } from "@/features/i18n/msAdlCopy";
import {
    buildFollowUpInitialValue,
    parseMsAdlRatingsScaleOptions,
} from "@/features/entry-values/utils/msAdlRatingsListHelpers";

interface MsAdlRatingsListProps {
    input: InputT;
    name: string;
    label?: string;
    description?: string;
    /** Page-1 selection input id; used on baseline scoring page to read chosen activities. */
    selectionInputId?: string;
}

const MsAdlRatingsList: React.FC<MsAdlRatingsListProps> = ({ input, name, label, description, selectionInputId }) => {
    const { watch, setValue } = useFormContext();
    const { formatActivity } = useMsAdlCopy();
    const { trackerId, entryId } = useParams({ strict: false });
    const [hasInitialized, setHasInitialized] = useState(false);

    const scale = parseMsAdlRatingsScaleOptions(input.scale_options);
    const currentValue = (watch(name) as Record<string, number>) || {};
    const selectedActivitiesFromForm = selectionInputId ? ((watch(selectionInputId) as string[]) ?? []) : [];

    const selectionFromStore = useEntryValuesStore((state) =>
        selectionInputId ? state.getEntryValueByInputId(selectionInputId)?.text_array : null
    );

    const {
        data: baselineState,
        isLoading,
        isError,
    } = useBaselineEntryValueByInputIdAndTrackerId(input.input_id, trackerId, entryId);

    const isFollowUp = baselineState?.isFollowUp ?? false;
    const lockedActivities = baselineState?.lockedActivities ?? [];

    const baselineSelectedActivities = useMemo(() => {
        if (isFollowUp) return lockedActivities;
        return selectedActivitiesFromForm.length > 0 ? selectedActivitiesFromForm : (selectionFromStore ?? []);
    }, [isFollowUp, lockedActivities, selectedActivitiesFromForm, selectionFromStore]);

    useEffect(() => {
        if (hasInitialized || isLoading || !baselineState) return;

        const hasCurrentValue = Object.keys(currentValue).length > 0;
        if (hasCurrentValue) {
            setHasInitialized(true);
            return;
        }

        if (isFollowUp) {
            setValue(name, buildFollowUpInitialValue(lockedActivities, scale.min));
            setHasInitialized(true);
            return;
        }

        if (baselineSelectedActivities.length > 0) {
            setValue(name, buildFollowUpInitialValue(baselineSelectedActivities, scale.min));
        }

        setHasInitialized(true);
    }, [
        baselineSelectedActivities,
        baselineState,
        currentValue,
        hasInitialized,
        isFollowUp,
        isLoading,
        lockedActivities,
        name,
        scale.min,
        setValue,
    ]);

    const sliderInput = useMemo(
        (): InputT => ({
            ...input,
            min_value: scale.min,
            max_value: scale.max,
            scale_options: [
                { label: "No difficulty", value: scale.min },
                { label: "Unable to do", value: scale.max },
            ],
        }),
        [input, scale.max, scale.min]
    );

    if (isLoading) {
        return (
            <section className="pt-4">
                <p className="text-muted-foreground text-lg">Loading activities...</p>
            </section>
        );
    }

    if (isError) {
        return (
            <section className="pt-4">
                <p className="text-destructive text-lg">Unable to load your activity list. Please try again.</p>
            </section>
        );
    }

    if (isFollowUp && lockedActivities.length === 0) {
        return (
            <section className="pt-4">
                <p className="text-destructive text-lg">
                    No baseline activities of daily living were found for this tracker. Complete the first entry before
                    submitting a follow-up.
                </p>
            </section>
        );
    }

    if (!isFollowUp && baselineSelectedActivities.length === 0) {
        return (
            <section className="pt-4">
                <p className="text-muted-foreground text-lg">
                    Go back and choose your activities of daily living before rating your difficulty.
                </p>
            </section>
        );
    }

    return (
        <section className="pt-4">
            <div className="space-y-6">
                {(label || description) && (
                    <div>
                        {label && (
                            <ui.Label className="font-semibold text-lg">
                                {label}
                                {input.is_optional && <OptionalMark />}
                            </ui.Label>
                        )}
                        {description && <p className="mt-1 text-muted-foreground text-lg">{description}</p>}
                        <p className="mt-2 text-muted-foreground text-lg">
                            Rate your current difficulty with each activity of daily living from {scale.min} (no
                            difficulty) to {scale.max} (unable to perform).
                        </p>
                    </div>
                )}

                {baselineSelectedActivities.map((activity) => {
                    const fieldName = `${name}.${activity}`;
                    const rating = currentValue[activity] ?? null;

                    return (
                        <Slider
                            key={activity}
                            input={sliderInput}
                            label={formatActivity(activity)}
                            fieldName={fieldName}
                            value={rating}
                            setValue={(value) => {
                                setValue(name, {
                                    ...currentValue,
                                    [activity]: value,
                                });
                            }}
                        />
                    );
                })}
            </div>
        </section>
    );
};

export default MsAdlRatingsList;
